import { Box, Card, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";

//import components
import TicketArrow from "../../common/ticket-arrow/ticket-arrow";

//import icons
import BusSvg from "../../../assets/icons/bus1.svg";

export default function BusItemLayout({
    labels = [],
    logoUrl,
    companyName,
    ticketDate,
    origin,
    destination,
    departureTime,
    renderFooter,
    renderExtraContent,
    isCardClickable,
    onCardClick,
}) {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const arrowWidth = isXs ? 70 : isSm ? 120 : 200;
    const clickable = !!isCardClickable?.({ isXs, isSm });

    return (
        <Card
            variant="outlined"
            sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                borderRadius: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                cursor: clickable ? "pointer" : "default",
            }}
            onClick={clickable ? onCardClick : null}
        >
            {/* Main information */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                {/* Labels */}
                {labels.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: { xs: 0.5, sm: 1 },
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.75, sm: 1 },
                        }}
                    >
                        {labels.map((label, index) => (
                            <Typography
                                key={`${label}-${index}`}
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    bgcolor: "grey.100",
                                    px: { xs: 0.75, sm: 1.5 },
                                    py: 0.5,
                                    borderRadius: 1,
                                    whiteSpace: "nowrap",
                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                }}
                            >
                                {label}
                            </Typography>
                        ))}
                    </Box>
                )}

                {/* Trip information */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(0, 170px) minmax(0, 1fr)",
                            md: "minmax(0, 230px) minmax(0, 1fr)",
                        },
                        alignItems: "center",
                        gap: { xs: 1.5, sm: 2, md: 3 },
                        px: { xs: 1, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    }}
                >
                    {/* Company */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
                        <Box
                            component="img"
                            src={logoUrl}
                            alt={companyName || "bus"}
                            sx={{
                                width: { xs: 32, sm: 40, md: 44 },
                                height: { xs: 32, sm: 40, md: 44 },
                                flexShrink: 0,
                                objectFit: "contain",
                                borderRadius: 1,
                            }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                    fontSize: { xs: "0.72rem", sm: "0.8rem", md: "0.875rem" },
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                }}
                                title={companyName}
                            >
                                {companyName}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontSize: { xs: "0.62rem", sm: "0.72rem", md: "0.8rem" },
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                }}
                            >
                                {ticketDate}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Route */}
                    <Box
                        sx={{
                            minWidth: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: { xs: 0.5, sm: 1, md: 1.5 },
                            pt: { xs: 1.5, sm: 2 },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flexShrink: 0,
                                maxWidth: { xs: "30%", sm: "35%" },
                                fontSize: { xs: "0.68rem", sm: "0.8rem", md: "0.9rem" },
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={origin}
                        >
                            {origin}
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: { xs: 40, sm: 50 },
                                maxWidth: { xs: 90, sm: 110, md: 370 },
                                position: "relative",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    top: { xs: -20, sm: -22 },
                                    transform: "translateX(-50%)",
                                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {departureTime}
                            </Typography>

                            <TicketArrow
                                width={arrowWidth}
                                icon={<img src={BusSvg} alt="" aria-hidden="true" />}
                                style={{ width: "100%", borderColor: "grey.400" }}
                            />
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                flexShrink: 0,
                                maxWidth: { xs: "30%", sm: "35%" },
                                fontSize: { xs: "0.68rem", sm: "0.8rem", md: "0.9rem" },
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={destination}
                        >
                            {destination}
                        </Typography>
                    </Box>
                </Box>

                {renderExtraContent && <Box sx={{ px: { xs: 1, sm: 2 }, pb: 1 }}>{renderExtraContent({ isXs, isSm })}</Box>}
            </Box>

            {/* Desktop divider */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
            {/* Mobile divider */}
            <Divider sx={{ display: { xs: "block", sm: "none" } }} />

            <Box
                sx={{
                    width: { xs: "100%", sm: 150, md: 190, lg: 220 },
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: { xs: "row", sm: "column" },
                    alignItems: "center",
                    justifyContent: { xs: "space-between", sm: "center" },
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    gap: 1,
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 1, sm: 2 },
                }}
            >
                {renderFooter({ isXs, isSm })}
            </Box>
        </Card>
    );
}
